<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <h1>
          {{ $tc('models.transaction', 2) }}
        </h1>
      </v-flex>

      <v-flex xs12>
        <v-card>
          <v-card-text>
            <v-layout wrap>
              <v-flex xs12 md4>
                <v-text-field v-model="shipmentId"
                  append-icon="search"
                  :label="$t('attrs.shipment_number')"
                  @click:append="fetch"
                  clearable
                  @keyup.enter.native="fetch"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md4>
                <v-text-field v-model="trackingNumber"
                  append-icon="search"
                  :label="$tc('attrs.tracking_number')"
                  @click:append="fetch"
                  clearable
                  @keyup.enter.native="fetch"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-data-table class="elevation-1"
          :headers="headers"
          :items="items"
          :loading="loading"
          :total-items="totalItems"
          :pagination.sync="pagination"
          :no-data-text="$t('noDataAvailable')">
          <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
          <template v-slot:items="props">
            <tr :class="{ 'no-border-bottom': !!props.item.description }">
              <td> {{ props.item.sequential_id || props.item.id }} </td>
              <td> {{ props.item.amount | currency }} </td>
              <td>
                <ShipmentLink :item="props.item"></ShipmentLink>
              </td>
              <td> {{ $t(`list.transactionTypes.${props.item.transaction_type}`) }} </td>
              <td> {{ $t(`list.statuses.${props.item.status}`) }} </td>
              <td> {{ props.item.created_at | date('lll') }} </td>
              <td>
                <v-btn elevation="2" outline color="success" @click="requestInvocie(props.item)" v-if="canInvoice(props.item)" :disabled="loading || disabledInvoice(props.item)">{{ $t('requestInvoice') }}</v-btn>
                <v-btn elevation="2" outline color="success" @click="downloadInvocie(props.item)" v-if="canDownloadInvoice(props.item)">{{ $t('download') }}</v-btn>

                <v-chip color="default" label outline v-if="invoiceRequested(props.item)">{{ $t('invoice_requested') }}</v-chip>
              </td>
            </tr>
            <tr v-if="props.item.description" class="no-border-top">
              <td colspan="8">
                {{ props.item.description }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Transaction from '@/client/transaction'
import Setting from '@/client/setting'
import Invoice from '@/client/invoice'

import ShipmentLink from '@/components/ShipmentLinkComp'

import { getMonthYear } from '@/utils.js'

export default {
  name: 'TransactionsView',

  props: {},

  data () {
    return {
      headers: [
        { text: '#', align: 'left', sortable: true, value: 'id', width: '5%' },
        { text: this.$t('attrs.amount'), align: 'left', sortable: true, value: 'amount' },
        { text: this.$tc('models.shipment'), align: 'left', sortable: true, value: 'shipment.id' },
        { text: this.$t('attrs.transaction_type'), align: 'left', sortable: true, value: 'transaction_type' },
        { text: this.$t('attrs.status'), align: 'left', sortable: true, value: 'status' },
        { text: this.$t('attrs.created_at'), align: 'left', sortable: true, value: 'created_at' },
        { text: '', align: 'right', sortable: false, value: 'actions' }
      ],
      items: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },
      loading: false,
      shipmentId: '',
      trackingNumber: '',
      showInvoicing: false
    }
  },

  components: {
    ShipmentLink,
  },

  computed: {
  },

  methods: {
    invoiceRequested (transaction) {
      const invoice = transaction.invoice
      if (!invoice) { return false }

      return invoice.status === 'pending'
    },

    async fetch () {
      let obj = {}
      this.loading = true
      const { sortBy, descending, page, rowsPerPage } = this.pagination
      let order = ''
      if (sortBy) {
        order = `${sortBy} ${descending ? 'desc' : 'asc'}`
      }

      obj = {
        page,
        per_page: rowsPerPage,
        order,
        shipment_id: this.shipmentId
      }

      if (this.shipmentId) obj.where = { shipment_id: this.shipmentId }

      if (this.trackingNumber)  {
        obj.tracking_number = true
        obj.include = ['labels']
        obj.where = { labels: {tracking_number: this.trackingNumber} }
      }  

      this.items = await Transaction.all(obj)
      this.totalItems = this.items.pagination.total_objects
      this.loading = false
    },

    async fetchInvoicingSetting () {
      const res = await Setting.byKey('invoicing')
      this.showInvoicing = res.value === '1'
    },

    canInvoice(transaction) {
      if (!this.showInvoicing) { return false }
      if (transaction.invoice) { return false }
      if (transaction.created_by === 'app') { return false }
      if (transaction.status !== 'cleared') { return false }
      if (transaction.transaction_type !== 'deposit') { return false }

      return true
    },

    canDownloadInvoice(transaction) {
      if (!this.showInvoicing) { return false }
      if (!transaction.invoice) { return false }
      if (transaction.invoice.status !== 'generated') { return false }

      return true
    },

    async requestInvocie(transaction) {
      this.loading = true
      try {
        await Invoice.requestInvoice({
          transaction_id: transaction.id
        })
        this.fetch()
      } catch (err) {
        this.handleErr(err)
      }
      this.loading = false
    },

    downloadFile (data, filename) {
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async downloadInvocie(transaction) {
      try {
        const data = await Invoice.download(transaction.invoice.id)
        this.downloadFile(data, `${transaction.invoice.id}.zip`)
      } catch (err) {
        this.handleErr(err)
      }
    },

    disabledInvoice(transaction) {
      const isSameMonth = this.isSameMonthTxn(transaction.created_at, new Date());
      return !isSameMonth
    },

    isSameMonthTxn(txnDate, currentDate) {
      const txn = getMonthYear(txnDate);
      const now = getMonthYear(currentDate);

      return txn.month === now.month && txn.year === now.year;
    }
  },

  watch: {
    pagination: {
      handler () {
        this.fetch()
      },
      deep: true
    },

    shipmentId () {
      if (!this.shipmentId) this.fetch()
    },

    trackingNumber () {
      if (!this.trackingNumber) this.fetch()
    }
  },

  async mounted () {
    await this.fetchInvoicingSetting()
    this.fetch()
  }
}
</script>
